import React, { useState } from "react";
import { Grid } from "@mui/material";
import { Close, Edit } from "@mui/icons-material";
import { UseFormReturn } from "react-hook-form";
import { QueryObserverResult, RefetchOptions } from "@tanstack/react-query";
import { useIntl } from "react-intl";

import { ListAction, ListActions } from "@framework/styled";
import { FormWrapper } from "@dzambalaorg/mui-form";
import { TextInput } from "@dzambalaorg/mui-inputs-core";
import { useMutationQuery } from "@dzambalaorg/react-hooks";

import { StyledSettingsListItem } from "../styled";
import { jsonFieldParser } from "../../../../utils/json-field-parser";

interface IEditRowDto {
  settings: Record<string, any>;
}

interface IEditableRowProps {
  row: Record<string, any>;
  refetch: (options?: RefetchOptions) => Promise<QueryObserverResult<Record<string, any>, Error>>;
}

export const EditableRow = ({ row, refetch }: IEditableRowProps) => {
  const [isEditable, setIsEditable] = useState(false);
  const { mutateAsync, isPending } = useMutationQuery<IEditRowDto>("/settings", "PUT");
  const { formatMessage } = useIntl();

  const toggleEditable = () => setIsEditable(prev => !prev);

  const onSubmit = async (values: Record<string, any>, form: UseFormReturn<any>) => {
    try {
      const parsedValue = await jsonFieldParser(values.settingsValue);
      await mutateAsync({ data: { settings: { [row.key]: parsedValue } } });
      await refetch();
      if (isEditable) {
        setIsEditable(false);
      }
    } catch (e) {
      form.setError("settingsValue", { message: formatMessage({ id: "form.validations.syntaxError" }) });
      console.error(e);
    }
  };

  return (
    <FormWrapper
      showButtons={isEditable}
      formSubmitButtonProps={{ disabled: isPending }}
      initialValues={{ settingsKey: row.key, settingsValue: row.value }}
      onSubmit={onSubmit}
      testId={"SettingCreate"}
    >
      <StyledSettingsListItem container size={12} alignItems="center" spacing={1}>
        <Grid sx={{ fontWeight: 500 }} size={{ lg: 3 }}>
          {row.key}
        </Grid>
        <Grid size={{ lg: 8 }}>{!isEditable ? JSON.stringify(row.value) : <TextInput name={"settingsValue"} />}</Grid>
        <Grid size={{ lg: 1 }}>
          <ListActions>
            <ListAction
              onClick={toggleEditable}
              message="form.buttons.edit"
              dataTestId="SettingEditButton"
              icon={!isEditable ? Edit : Close}
            />
          </ListActions>
        </Grid>
      </StyledSettingsListItem>
    </FormWrapper>
  );
};
